/* The Record tab's FLAT table and the position's LEDGER BOX (Ale rulings
 * 2026-09-05, flat v2 → v4).
 *
 *   RECORD  — one line per position (holder · instrument · issuer), in the
 *             plain order holder A–Z, then instrument A–Z. No grouping, no
 *             indentation, no hierarchy: the chain is a picture on the
 *             Overview tab, not a shape of this table.
 *   LEDGER  — clicking a line opens a box beneath the table: the position's
 *             movements (oldest first) and the instrument's movements
 *             (issuances / redemptions). A movement line = date · kind ·
 *             signed units · document · EV · state — no counterparty, no
 *             price, no running balance, no figure inside the name. Only
 *             CONFIRMED trades are movements: the officer's plug is a
 *             proposal until approved, so while the break is open it stays
 *             out of the ledger and the break chip stays on the line.
 *   FOOT    — listed movements are history, not an assumed zero opening.
 *             Only server-proved intervals between dated balance references
 *             can carry a break. The first approved balance is an anchor.
 *   COMMITMENT-BASED positions carry COMMITMENT · DRAWN · UNDRAWN columns
 *             instead of UNITS: a commitment signed writes commitment and
 *             undrawn; a capital call or drawdown writes drawn + and undrawn
 *             −; a distribution writes drawn − (and undrawn + only when the
 *             payload says it is recallable); the foot sums each column.
 *   COUNTS  — the toolbar chips (breaks · owed · changed since). Until the
 *             fund ledger endpoint lands they are derived from what the
 *             page reads today (`recordCounts`); `countsFromLedger` is the
 *             one-line switch to the ledger's own figures.
 *
 * Pure (imports only the equally pure `recordDerive`) so the bare node
 * runner can prove the arithmetic — see tests/unit/positionLedger.test.mjs. */

import {
  RECON_TASK_TYPES,
  deltaWords,
  figure,
  legPositionIds,
  tradesOfLeg,
  type DeriveSegment,
  type DeriveTask,
  type DeriveTrade,
} from './recordDerive.js'
import { tradeWords } from './evidenceRows.js'

/* ── inputs ─────────────────────────────────────────────────────────── */

export interface LedgerTrade extends DeriveTrade {
  transaction_type: string
  document: string | null
  /** A distribution the fund may call again — the payload says so; absent
   *  (or null) reads as "recallable: no", drawn only. */
  recallable?: boolean | null
}

export interface LedgerSegment extends DeriveSegment {
  holder_name: string | null
  commitment_based?: boolean
  state?: string
}

/* ── the order of the flat table ───────────────────────────────────── */

const byName = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').localeCompare(b ?? '', 'en', { sensitivity: 'base' })

/** Holder A–Z, then instrument A–Z, then issuer A–Z — the ONE order. */
export function flatOrder(
  a: { holder_name: string | null; instrument_name: string | null; issuer_name: string | null },
  b: { holder_name: string | null; instrument_name: string | null; issuer_name: string | null }
): number {
  return (
    byName(a.holder_name, b.holder_name) ||
    byName(a.instrument_name, b.instrument_name) ||
    byName(a.issuer_name, b.issuer_name)
  )
}

/* ── the line's balance document ───────────────────────────────────── */

interface BalanceDemandRow {
  object_id: string
  obligation_code: string
  status: string
  is_mandatory: boolean
  document_id?: string | null
  document_name?: string | null
}

/** The demand that states the line's balance — B2 (commitment
 *  confirmation) on a commitment-based position, else B1 (holder
 *  confirmation): the document the DOCUMENT column names, or what is
 *  owed there. The record with a document wins; else the first row. */
export function balanceDemand<R extends BalanceDemandRow>(
  seg: LedgerSegment,
  matrix: R[]
): R | null {
  const ids = new Set(legPositionIds(seg))
  const code = isCommitmentBased(seg) ? 'B2' : 'B1'
  const rows = matrix.filter((r) => ids.has(r.object_id))
  const pick = (c: string) =>
    rows.find((r) => r.obligation_code === c && (r.document_id || r.document_name)) ??
    rows.find((r) => r.obligation_code === c) ??
    null
  return pick(code) ?? pick(code === 'B2' ? 'B1' : 'B2')
}

/* ── movement kinds ─────────────────────────────────────────────────── */

export type MovementKind = 'inception' | 'commitment' | 'draw' | 'distribution' | 'other'

/** What a transaction type does to the position, using the ruled officer name. */
export function movementKind(transactionType: string | null | undefined): MovementKind {
  const w = tradeWords(transactionType).toLowerCase()
  if (/inception|opening/.test(w)) return 'inception'
  if (/commitment|subscription/.test(w)) return 'commitment'
  if (/capital call|drawdown|draw|call/.test(w)) return 'draw'
  if (/distribution|return of capital|redemption|repayment/.test(w)) return 'distribution'
  return 'other'
}

export function isCommitmentBased(seg: LedgerSegment): boolean {
  return seg.commitment_based === true || seg.committed_amount !== null
}

/** The three commitment columns one movement writes; `null` = the cell
 *  stays empty. */
export interface CommitmentEffect {
  commitment: number | null
  drawn: number | null
  undrawn: number | null
}

export function commitmentEffect(t: LedgerTrade): CommitmentEffect {
  const x = t.units_delta ?? 0
  const a = Math.abs(x)
  switch (movementKind(t.transaction_type)) {
    case 'commitment':
      return { commitment: x, drawn: null, undrawn: x }
    case 'draw':
      return { commitment: null, drawn: a, undrawn: -a }
    case 'distribution':
      return { commitment: null, drawn: -a, undrawn: t.recallable === true ? a : null }
    default:
      return { commitment: null, drawn: x, undrawn: null }
  }
}

/** The instrument's movements on a commitment-based paper: an issuance
 *  is the total commitments — it writes the COMMITMENT column only. */
export function supplyEffect(t: LedgerTrade): CommitmentEffect {
  return { commitment: t.units_delta ?? 0, drawn: null, undrawn: null }
}

export interface Movement {
  trade: LedgerTrade
  kind: MovementKind
  /** The signed units the line shows (an inception line is a starting
   *  line — unsigned). */
  units: number | null
  effect: CommitmentEffect | null
}

/** "+100", "−400", "400" for a starting line, "—" with no figure. */
export function unitsWords(m: Movement): string {
  if (m.units === null) return '—'
  if (m.kind === 'inception') return figure(Math.abs(m.units))
  return deltaWords(m.units)
}

/** A cell of the commitment columns: "+25,000,000", "−5,000,000", "" when
 *  the movement does not touch the column. */
export function effectWords(v: number | null): string {
  if (v === null) return ''
  return deltaWords(v)
}

const confirmed = (t: DeriveTrade) => t.state !== 'proposed'
const oldestFirst = (a: DeriveTrade, b: DeriveTrade) =>
  (a.effective_date ?? '').localeCompare(b.effective_date ?? '') || a.id.localeCompare(b.id)

function movement(t: LedgerTrade, commitment: boolean, supply: boolean): Movement {
  return {
    trade: t,
    kind: movementKind(t.transaction_type),
    units: t.units_delta,
    effect: commitment ? (supply ? supplyEffect(t) : commitmentEffect(t)) : null,
  }
}

/** The position's confirmed movements, oldest first. */
export function positionMovements(seg: LedgerSegment, trades: LedgerTrade[]): Movement[] {
  const cb = isCommitmentBased(seg)
  return (tradesOfLeg(seg, trades) as LedgerTrade[]).map((t) => movement(t, cb, false))
}

/** The instrument's confirmed movements — issuances and redemptions, the
 *  trades with no position — oldest first. */
export function instrumentMovements(seg: LedgerSegment, trades: LedgerTrade[]): Movement[] {
  if (!seg.instrument_id) return []
  const cb = isCommitmentBased(seg)
  return trades
    .filter((t) => confirmed(t) && t.position_id === null && t.instrument_id === seg.instrument_id)
    .sort(oldestFirst)
    .map((t) => movement(t, cb, true))
}

/* ── the ledger foot ────────────────────────────────────────────────── */

/** Server comparisons between approved dated checkpoints; no zero opening is inferred. */
export interface DatedMovementLedger {
  intervals: {
    previous: { id: string; date: string | null; value: number } | null
    latest: { id: string; date: string | null; value: number } | null
    expected: number | null
    residual: number | null
    status: 'first_reference' | 'balanced' | 'break' | 'unknown'
    movement_ids: string[]
    findings: string[]
  }[]
  findings: string[]
  current_value?: number | null
  current_date?: string | null
}

/** Never net opposite gaps across periods; the earliest real gap remains visible. */
export function datedLedgerBreak(ledger?: DatedMovementLedger | null): number | null {
  if (!ledger || ledger.intervals.length === 0) return null
  const broken = ledger.intervals.find((item) => item.status === 'break' && item.residual !== null)
  if (broken) return broken.residual
  if (
    ledger.findings.length ||
    ledger.intervals.some((item) => item.status === 'unknown' || item.findings.length)
  )
    return null
  return 0
}

export interface UnitsFoot {
  /** The sum of every movement listed. */
  movements: number
  stated: number | null
  /** Earliest measured dated-interval residual; null when comparison is unavailable. */
  break: number | null
  /** Movements dated after the statement — they roll the balance forward
   *  and do not enter the break. */
  later: number
}

const onOrBefore = (m: Movement, date: string | null) =>
  !date || (m.trade.effective_date ?? '') <= date

export function unitsFoot(
  movs: Movement[],
  stated: number | null,
  statedDate: string | null,
  ledger?: DatedMovementLedger | null
): UnitsFoot {
  const sum = (ms: Movement[]) => ms.reduce((n, m) => n + (m.units ?? 0), 0)
  const upTo = movs.filter((m) => onOrBefore(m, statedDate))
  return {
    movements: sum(movs),
    stated,
    break: stated === null ? null : datedLedgerBreak(ledger),
    later: movs.length - upTo.length,
  }
}

export interface CommitmentFoot {
  movements: CommitmentEffect
  stated: CommitmentEffect
  break: CommitmentEffect
  later: number
}

/** The column sums — but a column NO movement ever wrote stays null, so
 *  the foot leaves it blank instead of claiming a zero (an instrument's
 *  issuance touches COMMITMENT only; DRAWN and UNDRAWN are not "0", they
 *  are not part of this ledger). */
function sumEffects(ms: Movement[]): CommitmentEffect {
  const out: CommitmentEffect = { commitment: null, drawn: null, undrawn: null }
  for (const m of ms) {
    const e = m.effect ?? { commitment: null, drawn: null, undrawn: null }
    for (const k of ['commitment', 'drawn', 'undrawn'] as const)
      if (e[k] !== null) out[k] = (out[k] ?? 0) + (e[k] as number)
  }
  return out
}

export function commitmentFoot(
  movs: Movement[],
  stated: CommitmentEffect,
  statedDate: string | null,
  ledgers?: { commitment?: DatedMovementLedger | null; undrawn?: DatedMovementLedger | null }
): CommitmentFoot {
  const upTo = movs.filter((m) => onOrBefore(m, statedDate))
  const commitment = datedLedgerBreak(ledgers?.commitment)
  const undrawnResidual = datedLedgerBreak(ledgers?.undrawn)
  const undrawn = undrawnResidual === null ? null : -undrawnResidual
  return {
    movements: sumEffects(movs),
    stated,
    break: {
      commitment: stated.commitment === null ? null : commitment,
      // Drawn is derived from the two canonical legs; independent interval
      // residuals may cover different dates and must never be subtracted.
      drawn: null,
      undrawn: stated.undrawn === null ? null : undrawn,
    },
    later: movs.length - upTo.length,
  }
}

/** The stated figures of a commitment-based position, as the record
 *  holds them: the commitment, what is drawn, what is undrawn. */
export function statedCommitment(seg: LedgerSegment): CommitmentEffect {
  const commitment = seg.committed_amount
  const drawn = seg.drawn_amount
  const undrawn =
    seg.undrawn_amount ?? (commitment !== null && drawn !== null ? commitment - drawn : null)
  return { commitment, drawn, undrawn }
}

/** A break stands when any column differs from zero. */
export function hasBreak(brk: number | null | CommitmentEffect): boolean {
  if (brk === null) return false
  if (typeof brk === 'number') return brk !== 0
  return [brk.commitment, brk.drawn, brk.undrawn].some((v) => v !== null && v !== 0)
}

/* ── the toolbar counts ─────────────────────────────────────────────── */

export interface RecordCounts {
  breaks: number
  owed: number
  changed: number
  /** ISO date the "changed since" count runs from. */
  since: string
}

interface CountRow {
  status: string
}

/** What the fund ledger endpoint will serve — the structural subset the
 *  chips read (nolos/funds/schemas.py FundLedger). */
export interface LedgerCounts {
  breaks: { open: number }
  owed: { total: number }
  changed_since: { since: string; count: number }
}

/** THE SWITCH: the chips read the ledger's figures. */
export function countsFromLedger(l: LedgerCounts): RecordCounts {
  return {
    breaks: l.breaks.open,
    owed: l.owed.total,
    changed: l.changed_since.count,
    since: l.changed_since.since.slice(0, 10),
  }
}

/** Count and Changed filter use the same complete admitted position history. */
export function positionChangedSince(
  segment: LedgerSegment,
  trades: LedgerTrade[],
  since: string
): boolean {
  if (segment.state === 'proposed') return false
  const dates = [
    segment.as_of,
    ...tradesOfLeg(segment, trades).map((trade) => trade.effective_date),
  ]
  return dates.some((date) => !!date && date.slice(0, 10) >= since.slice(0, 10))
}

/** Until the ledger lands: the same figures from what the page reads —
 *  breaks = the open reconciliation tasks; owed = every unmet demand on
 *  the matrix (optional ones included, as the ledger counts them);
 *  changed = positions with a balance or a confirmed movement dated on
 *  or after `since`. */
export function recordCounts(input: {
  segments: LedgerSegment[]
  trades: LedgerTrade[]
  tasks: DeriveTask[]
  matrix: CountRow[]
  since: string
}): RecordCounts {
  const breaks = input.tasks.filter((t) => RECON_TASK_TYPES.has(t.task_type)).length
  const owed = input.matrix.filter(
    (r) => r.status !== 'satisfied' && r.status !== 'waived' && r.status !== 'pending_signoff'
  ).length
  const since = input.since.slice(0, 10)
  const changed = input.segments.filter((segment) =>
    positionChangedSince(segment, input.trades, since)
  ).length
  return { breaks, owed, changed, since }
}

/* ── the position line's own break (the RECO chip) ──────────────────── */

/** The open reconciliation task on the line's own records, if any — the
 *  door of the RECO chip and of the ledger foot's "reconcile ›". */
export function breakTaskOf(seg: LedgerSegment, tasks: DeriveTask[]): DeriveTask | null {
  const ids = new Set(legPositionIds(seg))
  return (
    tasks.find(
      (t) => RECON_TASK_TYPES.has(t.task_type) && t.target_id !== null && ids.has(t.target_id)
    ) ?? null
  )
}

/** The words of a break, short enough for the chip it rides in: "break
 *  9,000", "break −5m". A figure in the millions folds to "m" (the mock's
 *  own form) so the chip never truncates the number the officer needs. */
export function breakWords(delta: number): string {
  const sign = delta < 0 ? '−' : ''
  const a = Math.abs(delta)
  if (a >= 1_000_000) {
    const m = a / 1_000_000
    return `break ${sign}${Number.isInteger(m) ? m : m.toFixed(1)}m`
  }
  return `break ${sign}${figure(a)}`
}

/** "n positions · a on the fund, b through <holder>, c through <holder>". */
export function positionsFootWords(
  rows: { holder_id: string; holder_name: string | null }[],
  fundRootId: string | null
): string {
  const n = rows.length
  const head = `${n} position${n === 1 ? '' : 's'}`
  if (n === 0) return head
  const onFund = rows.filter((r) => r.holder_id === fundRootId).length
  const others = new Map<string, number>()
  for (const r of rows)
    if (r.holder_id !== fundRootId) {
      const k = r.holder_name ?? 'a holder'
      others.set(k, (others.get(k) ?? 0) + 1)
    }
  const parts: string[] = []
  if (onFund) parts.push(`${onFund} on the fund`)
  for (const [name, c] of [...others.entries()].sort((a, b) => byName(a[0], b[0])))
    parts.push(`${c} through ${name}`)
  return parts.length ? `${head} · ${parts.join(', ')}` : head
}
